import {
  CompanyRole,
  DeliverySourceEnum,
  DeliveryType,
  MediaShuttleUploadSources,
  SecurityLevel,
} from "@/__generated__/graphql";
import { useUserContext } from "@/contexts/UserContextProvider";
import { RESTRICTED_TIER_LEVEL } from "@/helpers/constants";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Field, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { UploadDeliveryFormValues } from "@/components/Actions/Deliver/UploadDelivery";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  UserEnabledForOfflineAccess,
  UserPassesSecurityConstrain,
} from "@/helpers/vendor";
import { InputField } from "@/components/common/InputField/InputField";
import SelectLocalFolderField from "@/components/common/SelectLocalFolderField/SelectLocalFolderField";
import { toast } from "sonner";
import { validateUpload } from "@/components/common/FileUpload/helpers";
import CopyTextField from "@/components/common/CopyTextField";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import Error from "@/components/common/Error";
import { PROJECT_COMPLETED_URL } from "@/helpers/url";

const uploadDeliverySchema = z
  .object({
    source: z.nativeEnum(DeliverySourceEnum),
    deliveryType: z.string().nonempty("Deliver type is required"),
    sftpHost: z.string().optional(),
    username: z.string().optional(),
    password: z.string().optional(),
    folderPath: z.string().optional(),
    files: z.array(z.instanceof(File)).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.source === DeliverySourceEnum.Ftp) {
      if (!data.sftpHost) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Host is required",
          path: ["sftpHost"],
        });
      }
      if (!data.username) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Username is required",
          path: ["username"],
        });
      }
      if (!data.password) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password is required",
          path: ["password"],
        });
      }
      if (!data.folderPath) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Folder path is required",
          path: ["folderPath"],
        });
      }
    }

    if (
      [
        DeliverySourceEnum.S3,
        DeliverySourceEnum.Ftp,
        DeliverySourceEnum.SecureVm,
      ].includes(data.source)
    ) {
      if (!data.folderPath) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Folder path is required",
          path: ["folderPath"],
        });
      }
    }

    if (data.source === DeliverySourceEnum.Http) {
      if (!data.files?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Files are required",
          path: ["files"],
        });
      }
    }
  });

const LAST_UPLOAD_LINK = gql`
  query LastMediaShuttleUploadPath(
    $projectId: SmartID
    $vendorProjectId: SmartID
  ) {
    lastMediaShuttleUploadPath(
      projectId: $projectId
      vendorProjectId: $vendorProjectId
    ) {
      id
      folderPath
    }
  }
`;

const GENERATE_UPLOAD_LINK = gql`
  mutation GenerateUploadLink(
    $vendorProjectId: SmartID
    $projectId: SmartID
    $source: MediaShuttleUploadSources!
  ) {
    generateUploadLink(
      vendorProjectId: $vendorProjectId
      projectId: $projectId
      source: $source
    ) {
      success
      message
      uploadUrl
      uploadedPath
    }
  }
`;

function UserUploadDelivery({
  onNext,
  onFilesSelected,
}: {
  onNext: (
    values: UploadDeliveryFormValues,
    formikHelpers: FormikHelpers<UploadDeliveryFormValues>,
  ) => void;
  onFilesSelected?: (files: File[]) => void;
}) {
  const { currentUser, currentVendorProject, role } = useUserContext();
  const [lastUploadedPath, setLastUploadedPath] = useState("");
  const navigate = useNavigate();

  const INITIAL_VALUES = {
    source: DeliverySourceEnum.S3,
    sftpHost: "",
    username: "",
    password: "",
    folderPath: "",
    files: [],
    deliveryType: DeliveryType.Final,
  };

  useEffect(() => {
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      progressBar.style.visibility = "hidden";
    }
  }, []);

  return (
    <Formik<UploadDeliveryFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={toFormikValidationSchema(uploadDeliverySchema)}
      validateOnMount
      onSubmit={(values, formikHelpers) => {
        onNext(values, formikHelpers);
      }}
    >
      {({
        isSubmitting,
        submitForm,
        values,
        setFieldValue,
        isValid,
        status,
      }) => (
        <div className="flex flex-col h-full">
          <div className="p-6 flex flex-col gap-6 bg-quinary overflow-y-auto flex-grow">
            {![DeliverySourceEnum.Ftp, DeliverySourceEnum.InternalS3].includes(
              values.source,
            ) &&
              !values.folderPath && (
                <div className="bg-secondary p-6 text-secondary-foreground flex gap-6 rounded-md">
                  <Info className="invert-50 dark:invert-0 size-6" />
                  <div className="flex-1 text-sm/loose [&_a]:text-paint [&_strong]:text-primary-foreground [&_strong]:font-semibold">
                    {values.source === DeliverySourceEnum.S3 && (
                      <>
                        <p>
                          Please upload your delivery packages on the same SFTP
                          where you downloaded the source footage.
                        </p>
                        <p>
                          After the upload is complete, enter the full path of
                          the folder in which you have placed your delivery
                          packages.
                        </p>
                        <p>
                          <span className="font-medium text-primary-foreground">
                            Do not click
                          </span>{" "}
                          next until the upload is complete.
                        </p>
                      </>
                    )}

                    {values.source === DeliverySourceEnum.Http && (
                      <>
                        <p>
                          Arrange each task in its own folder. See delivery
                          instruction
                          <a
                            href="https://thehotspring.com/support/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            &nbsp;here
                          </a>
                        </p>
                        <p className="text-primary-foreground">
                          <strong>Note: </strong>Drag and drop the task folder
                          to deliver in the space below.
                        </p>
                      </>
                    )}

                    {values.source === DeliverySourceEnum.SecureVm && (
                      <>
                        <p>
                          Please enter the Folder path where you have placed
                          your delivery files on your Secure VM
                        </p>
                        <p>
                          <strong>Note: </strong> Only enter the part of the
                          path after the main Project path, with no leading
                          slash ('/')
                        </p>
                        <p>
                          <strong>E.g: </strong> 2021_04_04/
                        </p>
                      </>
                    )}

                    {values.source === DeliverySourceEnum.MediaShuttle && (
                      <div className="w-full flex [&>div]:px-6">
                        <div className="flex-1">
                          <strong>Step 1: Generate upload link</strong>
                          <p>Generate an upload link using the button below.</p>
                        </div>
                        <div className="flex-1 border-x border-x-secondary-icon">
                          <strong>Step 2: Upload your delivery package</strong>
                          <p>
                            Arrange each task delivery in its own folder. Follow
                            delivery instruction
                            <a
                              href="https://thehotspring.com/support/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              &nbsp;here
                            </a>
                            . Once organized, upload them using the upload link.
                          </p>
                        </div>
                        <div className="flex-1">
                          <strong>
                            Step 3: Enter upload path and delivery
                          </strong>
                          <p>
                            Enter the path where the delivery files were
                            uploaded (Will be in the{" "}
                            <strong>Last upload path</strong> field) and click
                            Next.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            <div className="bg-primary rounded-md border p-10 flex flex-col gap-10">
              <div>
                <div className="font-medium text-xl">Upload from:</div>
                <div className="mt-5">
                  <RadioGroup
                    className="flex items-center justify-between [&_label]:cursor-pointer [&_label]:text-secondary-foreground [&_button]:bg-accent"
                    value={values.source}
                    defaultValue={values.source}
                    onValueChange={(value) => {
                      setFieldValue("source", value);
                      localStorage.setItem("fileSelectionMethod", value);
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value={DeliverySourceEnum.S3} id="S3" />
                      <label htmlFor="S3">Project FTP</label>
                    </div>

                    {currentVendorProject &&
                      UserPassesSecurityConstrain(
                        role as CompanyRole,
                        {
                          securityLevel:
                            currentVendorProject.project.securityLevel,
                        },
                        currentUser,
                      ) && (
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={DeliverySourceEnum.Http}
                            id="Desktop"
                          />
                          <label htmlFor="Desktop">Desktop</label>
                        </div>
                      )}

                    {currentUser?.office?.company.profile
                      .isFtpUploadEnabledForDelivery && (
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={DeliverySourceEnum.Ftp}
                          id="Ftp"
                        />
                        <label htmlFor="Ftp">External FTP</label>
                      </div>
                    )}

                    {currentVendorProject &&
                      RESTRICTED_TIER_LEVEL.includes(
                        currentVendorProject.project.securityLevel,
                      ) &&
                      role === CompanyRole.Vendor &&
                      UserEnabledForOfflineAccess(
                        role as CompanyRole,
                        {
                          securityLevel:
                            currentVendorProject.project.securityLevel,
                        },
                        currentUser,
                      ) && (
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={DeliverySourceEnum.SecureVm}
                            id="SecureVm"
                          />
                          <label htmlFor="SecureVm">Secure VM</label>
                        </div>
                      )}

                    {currentVendorProject?.project.securityLevel !==
                      SecurityLevel.Tier_1 && (
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={DeliverySourceEnum.MediaShuttle}
                          id="MediaShuttle"
                        />
                        <label htmlFor="MediaShuttle">Media Shuttle</label>
                      </div>
                    )}
                  </RadioGroup>
                </div>
              </div>

              <div className="flex gap-10">
                <div className="flex-2 flex flex-col gap-4">
                  {values.source === DeliverySourceEnum.Ftp && (
                    <>
                      <div className="flex items-center gap-8">
                        <div className="font-medium w-40">Host</div>
                        <Field
                          name="sftpHost"
                          component={InputField}
                          placeholder="Host"
                          className="flex-1"
                          inputClassName="!bg-tertiary text-muted-foreground"
                        />
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="font-medium w-40">Username</div>
                        <Field
                          name="username"
                          component={InputField}
                          placeholder="Username"
                          className="flex-1"
                          inputClassName="!bg-tertiary text-muted-foreground"
                        />
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="font-medium w-40">Password</div>
                        <Field
                          name="password"
                          component={InputField}
                          placeholder="Password"
                          className="flex-1"
                          inputClassName="!bg-tertiary text-muted-foreground"
                          type="password"
                        />
                      </div>
                    </>
                  )}

                  {[
                    DeliverySourceEnum.S3,
                    DeliverySourceEnum.Ftp,
                    DeliverySourceEnum.SecureVm,
                  ].includes(values.source) && (
                    <div className="flex items-center gap-8">
                      <div className="font-medium w-40">Folder Path</div>
                      <Field
                        name="folderPath"
                        component={InputField}
                        placeholder="Folder Path"
                        className="flex-1"
                        inputClassName="!bg-tertiary text-muted-foreground"
                      />
                    </div>
                  )}

                  {values.source === DeliverySourceEnum.Http && (
                    <div className="flex items-center gap-8">
                      <div className="font-medium w-40">Upload Folder</div>
                      <SelectLocalFolderField
                        onSuccess={async (files) => {
                          const { isValid, suspect } =
                            await validateUpload(files);
                          if (!isValid) {
                            toast.error(
                              `${suspect || "A file/folder"} is empty. Please recheck.`,
                            );
                            return;
                          }
                          setFieldValue("files", files);
                          onFilesSelected?.(files);
                          setTimeout(() => submitForm(), 500);
                        }}
                        className="flex-1"
                      />
                    </div>
                  )}

                  {values.source === DeliverySourceEnum.MediaShuttle && (
                    <>
                      <div className="flex items-center gap-8">
                        <div className="font-medium w-40">Path</div>
                        <Field
                          name="folderPath"
                          component={InputField}
                          placeholder="Path"
                          className="flex-1"
                          inputClassName="!bg-tertiary text-muted-foreground"
                        />
                      </div>

                      <div className="flex items-center gap-8">
                        <div className="font-medium w-40">Last Upload Path</div>
                        <div className="flex-1 flex items-center gap-2">
                          <CopyTextField
                            value={lastUploadedPath}
                            className="flex-1"
                          />
                          <Tooltip>
                            <TooltipTrigger className="border p-2 rounded-sm">
                              <MessageCircleQuestion
                                size={14}
                                className="text-secondary-foreground"
                              />
                            </TooltipTrigger>
                            <TooltipContent className="p-3 text-xs text-secondary-foreground">
                              <div className="w-60">
                                This is the path where your content is uploaded.
                                It will always be the path the last time the
                                upload link was generated
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {values.source === DeliverySourceEnum.MediaShuttle && (
                  <div className="flex-1">
                    <UploadDeliveryMediaShuttle
                      vendorProjectId={currentVendorProject?.id}
                      source={MediaShuttleUploadSources.ArtistUploadDelivery}
                      setUploadedPath={setLastUploadedPath}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-primary border-t flex justify-end gap-4 py-4 px-6 flex-shrink-0">
            <Error error={status?.error} />
            <Button
              onClick={() => {
                if (currentVendorProject)
                  navigate(
                    generatePath(PROJECT_COMPLETED_URL, {
                      project_id: currentVendorProject.id,
                    }),
                  );
              }}
            >
              Back
            </Button>

            <Button
              onClick={() => submitForm()}
              disabled={isSubmitting || !isValid}
              className="bg-service-paint hover:bg-service-paint/96 text-white"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
}

function UploadDeliveryMediaShuttle({
  vendorProjectId,
  source,
  setUploadedPath,
}: {
  vendorProjectId?: string;
  source: MediaShuttleUploadSources;
  setUploadedPath: (path: string) => void;
}) {
  const { currentProject } = useUserContext();
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [formError, setFormError] = useState("");
  const [uploadLink, setUploadLink] = useState("");

  const [generateUploadLink] = useMutation(GENERATE_UPLOAD_LINK);

  const { loading, data } = useQuery(LAST_UPLOAD_LINK, {
    variables: {
      projectId: currentProject?.id,
      vendorProjectId: vendorProjectId as string,
    },
    fetchPolicy: "network-only",
  });

  const handleGenerateLink = async () => {
    setIsGeneratingLink(true);
    setUploadLink("");
    try {
      const result = await generateUploadLink({
        variables: {
          vendorProjectId,
          projectId: currentProject?.id,
          source,
        },
      });

      if (result && result.data && result.data.generateUploadLink) {
        const { success, message, uploadUrl, uploadedPath } =
          result.data.generateUploadLink;
        if (success && uploadUrl) {
          setUploadLink(uploadUrl);
          setUploadedPath(uploadedPath as string);
        } else {
          setFormError(String(message));
        }
      }
    } catch (err) {
      setFormError(String(err));
    } finally {
      setIsGeneratingLink(false);
    }
  };

  useEffect(() => {
    if (data && !loading && !uploadLink && !isGeneratingLink) {
      if (data.lastMediaShuttleUploadPath) {
        setUploadedPath(data.lastMediaShuttleUploadPath.folderPath as string);
      }
    }
  }, [data, isGeneratingLink, loading, setUploadedPath, uploadLink]);

  return (
    <div className="p-4 flex flex-col gap-4 items-center border rounded-md">
      {!isGeneratingLink ? (
        <div className="text-sm text-center">
          {uploadLink
            ? "Your upload link is ready—use it to submit your delivery."
            : "Generate an upload link by clicking below"}
        </div>
      ) : (
        <div className="text-sm">
          <span>Your upload link is getting generated...</span>
        </div>
      )}

      <Error error={formError} />

      {uploadLink ? (
        <>
          <CopyTextField value={uploadLink} />

          <Button
            className="border border-link text-link"
            onClick={() => window.open(uploadLink, "_blank")}
          >
            Open link
          </Button>
        </>
      ) : (
        <>
          <Button
            disabled={isGeneratingLink}
            onClick={handleGenerateLink}
            className="text-white bg-service-paint hover:bg-service-paint/96"
          >
            <span>
              {isGeneratingLink ? "Generating..." : "Generate Upload Link"}
            </span>
          </Button>
        </>
      )}
    </div>
  );
}

export default UserUploadDelivery;
